/**
 * Подписи на линиях, каналах и фигурах.
 *
 * Линию подписывают прямо на ней: «поддержка недели», «пробой — шорт».
 * Надпись идёт вдоль линии, над ней, под тем же углом — как пишут от
 * руки на распечатке графика. У канала — над верхней границей, у эллипса
 * и круга — по центру.
 *
 * Выделенная линия под курсором показывает подсказку «+ Добавьте текст»:
 * щелчок по ней — и надпись набирается прямо на графике.
 *
 * Рисовальщик фигуры только отмечает место (`CaptionSpot`); саму надпись
 * рисует слой — он знает, выделена ли фигура, наведена ли она и не
 * набирают ли её текст прямо сейчас.
 */

import { withAlpha } from './color.js';
import type { TextStyle } from './text-style.js';

/** Где подписывать: середина линии и её наклон. */
export interface CaptionSpot {
  readonly x: number;
  readonly y: number;
  /** Наклон линии, радианы; надпись всегда читается слева направо. */
  readonly angle: number;
  /** Над линией — или по центру фигуры. */
  readonly place: 'above' | 'center';
}

/** Занятое надписью место — повёрнутый прямоугольник. */
export interface CaptionBox {
  readonly x: number;
  readonly y: number;
  readonly angle: number;
  readonly width: number;
  readonly height: number;
}

/** У каких инструментов надпись живёт на самой линии. */
export const CAPTION_KINDS: ReadonlySet<string> = new Set([
  'trend',
  'ray',
  'extended',
  'info-line',
  'trend-angle',
  'horizontal',
  'horizontal-ray',
  'vertical',
  'parallel-channel',
  'flat-channel',
  'disjoint-channel',
  'regression',
  'circle',
  'ellipse',
  // Траектория и ломаная — подпись над самым длинным отрезком.
  'path',
  'polyline',
]);

export function hasCaption(kind: string): boolean {
  return CAPTION_KINDS.has(kind);
}

/** Подсказка на пустой выделенной линии. */
export const CAPTION_PLACEHOLDER = 'Добавьте текст';

/** Наклон, при котором текст читается слева направо, а не вверх ногами. */
export function readableAngle(angle: number): number {
  if (angle > Math.PI / 2) return angle - Math.PI;
  if (angle <= -Math.PI / 2) return angle + Math.PI;
  return angle;
}

/** Место над отрезком: середина (или своя доля пути) и наклон. */
export function spotOn(
  a: { x: number; y: number },
  b: { x: number; y: number },
  share = 0.5,
): CaptionSpot {
  return {
    x: a.x + (b.x - a.x) * share,
    y: a.y + (b.y - a.y) * share,
    angle: readableAngle(Math.atan2(b.y - a.y, b.x - a.x)),
    place: 'above',
  };
}

/**
 * Место на ломаной: над самым длинным отрезком.
 *
 * На коротком колене подпись свисала бы с обоих концов и ложилась на
 * соседние отрезки; на длинном ей просторно, и её видно сразу.
 */
export function spotOnLongest(points: ReadonlyArray<{ x: number; y: number }>): CaptionSpot | null {
  let best: CaptionSpot | null = null;
  let longest = -1;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length > longest) {
      longest = length;
      best = spotOn(a, b);
    }
  }
  return best;
}

/** Шрифт надписи: чуть плотнее обычного — линия тонкая, текст не должен теряться. */
function captionFont(style: TextStyle): string {
  const slant = style.italic ? 'italic ' : '';
  return `${slant}${style.bold ? 700 : 500} ${style.fontSize}px Onest, system-ui, sans-serif`;
}

/** Точка внутри повёрнутого прямоугольника (с запасом в несколько пикселей). */
export function insideCaption(box: CaptionBox, x: number, y: number, slack = 4): boolean {
  const cos = Math.cos(-box.angle);
  const sin = Math.sin(-box.angle);
  const dx = x - box.x;
  const dy = y - box.y;
  const localX = dx * cos - dy * sin;
  const localY = dx * sin + dy * cos;
  return Math.abs(localX) <= box.width / 2 + slack && Math.abs(localY) <= box.height / 2 + slack;
}

/** Центр надписи: над линией — на полстроки и зазор выше неё. */
function centerOf(spot: CaptionSpot, height: number): { x: number; y: number } {
  if (spot.place === 'center') return { x: spot.x, y: spot.y };
  const lift = height / 2 + 8;
  // «Выше» в повёрнутой системе — против нормали вниз.
  return { x: spot.x + Math.sin(spot.angle) * lift, y: spot.y - Math.cos(spot.angle) * lift };
}

/**
 * Надпись на линии.
 *
 * Тёмная обводка букв отделяет текст от свечей под ним — без плашки,
 * которая закрыла бы график. Подложка — только если её включили.
 */
export function drawCaption(
  ctx: CanvasRenderingContext2D,
  spot: CaptionSpot,
  text: string,
  style: TextStyle,
  color: string,
): CaptionBox {
  const lines = text.split('\n');
  ctx.save();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  ctx.font = captionFont(style);
  const lineHeight = Math.round(style.fontSize * 1.3);
  const width = Math.max(...lines.map((line) => ctx.measureText(line).width));
  const height = lineHeight * lines.length;
  const padX = style.background || style.border ? 8 : 2;
  const padY = style.background || style.border ? 4 : 1;
  const center = centerOf(spot, height + padY * 2);

  ctx.translate(center.x, center.y);
  ctx.rotate(spot.angle);

  if (style.background || style.border) {
    ctx.beginPath();
    ctx.roundRect(-width / 2 - padX, -height / 2 - padY, width + padX * 2, height + padY * 2, 7);
    if (style.background) {
      ctx.globalAlpha = style.backgroundOpacity;
      ctx.fillStyle = style.backgroundColor;
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    if (style.border) {
      ctx.lineWidth = 1;
      ctx.strokeStyle = color;
      ctx.stroke();
    }
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  lines.forEach((line, index) => {
    const y = -height / 2 + lineHeight * (index + 0.5);
    if (!style.background) {
      ctx.lineWidth = 3.5;
      ctx.strokeStyle = 'rgba(7, 11, 22, 0.88)';
      ctx.strokeText(line, 0, y);
    }
    ctx.fillStyle = color;
    ctx.fillText(line, 0, y);
  });
  ctx.restore();

  return { x: center.x, y: center.y, angle: spot.angle, width: width + padX * 2, height: height + padY * 2 };
}

/**
 * Подсказка «+ Добавьте текст» — на выделенной линии под курсором.
 *
 * Полупрозрачная, с плюсом в кружке: видно, что это действие, а не
 * надпись, и что по нему можно щёлкнуть.
 */
export function drawCaptionPlaceholder(
  ctx: CanvasRenderingContext2D,
  spot: CaptionSpot,
  style: TextStyle,
  color: string,
): CaptionBox {
  ctx.save();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  const size = Math.max(11, Math.min(style.fontSize, 14));
  ctx.font = `500 ${size}px Onest, system-ui, sans-serif`;
  const textWidth = ctx.measureText(CAPTION_PLACEHOLDER).width;
  const plus = size + 2;
  const gap = 6;
  const width = plus + gap + textWidth + 16;
  const height = size + 12;
  const center = centerOf(spot, height);

  ctx.translate(center.x, center.y);
  ctx.rotate(spot.angle);

  // Стеклянная капсула с кромкой цвета линии.
  ctx.beginPath();
  ctx.roundRect(-width / 2, -height / 2, width, height, height / 2);
  ctx.fillStyle = 'rgba(12, 17, 33, 0.82)';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = withAlpha(color, 0.55);
  ctx.stroke();

  // Плюс в кружке.
  const px = -width / 2 + 8 + plus / 2;
  ctx.beginPath();
  ctx.arc(px, 0, plus / 2, 0, Math.PI * 2);
  ctx.fillStyle = withAlpha(color, 0.22);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(px - plus * 0.22, 0);
  ctx.lineTo(px + plus * 0.22, 0);
  ctx.moveTo(px, -plus * 0.22);
  ctx.lineTo(px, plus * 0.22);
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.strokeStyle = color;
  ctx.stroke();

  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(234, 240, 251, 0.78)';
  ctx.fillText(CAPTION_PLACEHOLDER, px + plus / 2 + gap, 0.5);
  ctx.restore();

  return { x: center.x, y: center.y, angle: spot.angle, width, height };
}

